import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { EYesNo } from '../../../../../shared/models/types.enum';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'intervention-monitoring-consideration',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './intervention-monitoring-consideration.component.html',
  styleUrl: './intervention-monitoring-consideration.component.css'
})
export class InterventionMonitoringConsiderationComponent implements OnInit, OnChanges {

  @Input() rmsPlanChecklistData: any;
  @Output() onSaveSection4 = new EventEmitter<any>();

  yesNo = EYesNo;
  considerationForm: FormGroup = new FormGroup([]);
  checkList = [
    {      
      label: 'Increased contact/monitoring of subject (Routine check-ins - Daily, Weekly, Other)',
      formControlName: 'increased_contact',
      notes: 'increased_contact_notes'
    },
    {      
      label: 'Travel card to hold accountable for whereabouts and on-time arrival to destinations',
      formControlName: 'travel_card',
      notes: 'travel_card_notes'
    },
    {      
      label: 'Backpack, coat, and other belongings checked in/out',
      formControlName: 'backpack',
      notes: 'backpack_planning_notes'
    },
    {      
      label: 'Late arrival and/or early dismissal',  
      formControlName: 'late_arrival',
      notes: 'late_arrival_planning_notes'
    },
    {      
      label: 'Increased supervision in specific settings',  
      formControlName: 'increased_supervision',
      notes: 'increased_supervision_notes'
    },
    {      
      label: 'Modify Daily Schedule',  
      formControlName: 'modify_schedule',
      notes: 'modify_schedule_notes'
    },
    {      
      label: 'Plan to address harm to self or others created',  
      formControlName: 'address_harm_self',
      notes: 'address_harm_self_notes'
    },
    {      
      label: 'Plan to address future behavior',  
      formControlName: 'future_behavior',
      notes: 'future_behavior_notes'
    },
    {      
      label: 'Plan to address safety',  
      formControlName: 'safety',
      notes: 'safety_notes'
    },
    {      
      label: 'Plan to address containment',  
      formControlName: 'containment',
      notes: 'containment_notes'
    },
    {      
      label: 'Intervention by support staff (Psychologist, Social Worker, Counselor, MTSS Team, etc.)',  
      formControlName: 'support_staff_intervention',
      notes: 'support_staff_intervention_plan'
    },
    {      
      label: 'Behavioral assessment',  
      formControlName: 'behavioral_assessment',
      notes: 'behavioral_assessment_notes'
    },
    {      
      label: 'Positive reinforcements for positive behavior (ROAR should include list of positive behaviors and agreed-upon reinforcements)',  
      formControlName: 'positive_reinforcement',
      notes: 'positive_reinforcement_notes'
    },
    {      
      label: 'Peer or affective needs support group',  
      formControlName: 'support_group',
      notes: 'support_group_notes'
    },
    {      
      label: 'Peer support',  
      formControlName: 'peer_support',
      notes: 'peer_support_notes'
    },
    {      
      label: 'Intervention by community agency (Probation, Diversion, Child Welfare, 1451, etc.)',  
      formControlName: 'community_intervention',
      notes: 'community_intervention_notes'
    },
    {      
      label: 'Identify precipitating/aggravating circumstances and create intervention to alleviate tension',  
      formControlName: 'alleviate_circumstances',
      notes: 'alleviate_circumstances_notes'
    },
    {      
      label: 'Drug and/or alcohol intervention',  
      formControlName: 'intervention',
      notes: 'intervention_notes'
    },
    {      
      label: 'Referral to intervention team',  
      formControlName: 'intervention_team',
      notes: 'intervention_team_notes'
    },
    {      
      label: 'Pro-social discipline (Restorative Justice, community service, etc.)',  
      formControlName: 'discipline',
      notes: 'discipline_notes'
    },
    {      
      label: 'Schedule review of IEP or 504 to review goals and placement options',  
      formControlName: 'schedule_review',
      notes: 'schedule_review_notes'
    },
    {      
      label: 'Virtual/Distance Learning',  
      formControlName: 'virtual_learning',
      notes: 'virtual_learning_notes'
    },
    {      
      label: 'Alternative School/Setting',  
      formControlName: 'alternative_school',
      notes: 'alternative_school_notes'
    },
    {      
      label: 'Other Actions',  
      formControlName: 'other_actions',
      notes: 'other_actions_notes'
    }
  ]

  constructor(
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rmsPlanChecklistData'] && changes['rmsPlanChecklistData'].currentValue) {
      this.initializeForm();
    }
  }

  private initializeForm() {
    let group: any = {};
    
    this.checkList.forEach(item => {

      group[item.formControlName] = new FormControl(this.rmsPlanChecklistData?.[item.formControlName] || Number(this.yesNo.No));
      group[item.notes] = new FormControl(this.rmsPlanChecklistData?.[item.notes] || '');
      if (item.formControlName === 'increased_contact') {
        group['increased_contact_check_in'] = new FormControl(this.rmsPlanChecklistData?.increased_contact_check_in || '');
      }
    });

    group['intervention_roar_needed'] = new FormControl(String(this.rmsPlanChecklistData?.intervention_roar_needed) || this.yesNo.No, Validators.required);
    this.considerationForm = new FormGroup(group);

    this.considerationForm = new FormGroup(group, { validators: this.atLeastOneCheckboxCheckedValidator() });
    this.setupFormChangeHandlers();
  }

  private setupFormChangeHandlers(): void {
    this.checkList.forEach(item => {
      const checkboxControl = this.considerationForm.get(item.formControlName);
      if (checkboxControl) {
        checkboxControl.valueChanges.subscribe((value) => {
          const convertedValue = value ? 1 : 0; // Convert true/false to 1/0
          checkboxControl.setValue(convertedValue, { emitEvent: false });
  
          if (item.formControlName === 'increased_contact') {
            this.handleIncreasedContactChange(convertedValue);
          } else {
            this.handleOtherItemsChange(convertedValue, item);
          }
        });
      }
    });
    this.cdr.detectChanges();
  }

  private handleIncreasedContactChange(convertedValue: number) {
    const checkInControl = this.considerationForm.get('increased_contact_check_in');
    const notesControl = this.considerationForm.get('increased_contact_notes');
  
    if (convertedValue === 1) {
      // When 'increased_contact' is checked (1)
      checkInControl?.setValidators(Validators.required);
  
      checkInControl?.valueChanges.subscribe((checkInValue) => {
        if (checkInValue === 'Other') {
          notesControl?.setValidators(Validators.required);
        } else {
          notesControl?.clearValidators();
          notesControl?.setValue(''); // Clear the value if it's not 'Other'
        }
        notesControl?.updateValueAndValidity(); // Trigger validation update
      });
    } else {
      // If 'increased_contact' is unchecked (0)
      checkInControl?.clearValidators();
      checkInControl?.setValue('');
      notesControl?.clearValidators();
      notesControl?.setValue('');
    }
  
    checkInControl?.updateValueAndValidity(); // Trigger validation update for 'increased_contact_check_in'
  }

  private handleOtherItemsChange(convertedValue: number, item: any) {
    const notesControl = this.considerationForm.get(item.notes);
    if (convertedValue === 1) {
      notesControl?.setValidators(Validators.required);
    } else {
      notesControl?.clearValidators();
      notesControl?.setValue('');
    }
    notesControl?.updateValueAndValidity(); 
  }

  private atLeastOneCheckboxCheckedValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const formGroup = control as FormGroup;
  
      // Check if at least one checkbox in the checkList is checked
      const isAtLeastOneChecked = this.checkList.some(item => formGroup.get(item.formControlName)?.value === 1);
  
      return isAtLeastOneChecked ? null : { noCheckboxChecked: true };
    };
  }

  saveConsideration(): void {
    if (this.considerationForm.valid) {
      this.onSaveSection4.emit(this.considerationForm.value);
    }
  }

}
